import { memo, useId } from 'react'

import i18n from 'i18n-literally'

import { ChevronLeft } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'

import { WebDialog } from '~/src/Dialog'
import type { AroyaDialogProps } from '~/src/Dialog'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import getClassPrefixer from '~/src/UI/classPrefixer'
import Button from '~/src/UI/Shared/Button'

const classes = getClassPrefixer('Dialog')

const MobileWebDialog = styled((props: AroyaDialogProps) => <WebDialog {...props} />)({
  [`&& .${classes.root}.MuiDialog-paperFullScreen`]: {
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
    paddingRight: 'max(env(safe-area-inset-right, 0px), 6px)',
    paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 6px)',
    paddingLeft: 'max(env(safe-area-inset-left, 0px), 6px)',
  },
  "&& .MuiDialog-paperFullScreen [data-testid='aroya-close-button']": { display: 'none' },
})

const Header = styled('div')(({ theme }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  gap: theme.spacing(1),
}))

const BackButton = styled(IconButton)({
  padding: 0,
  '&&&&': { display: 'inline-flex' },
})

const HeaderTitle = styled('div')({
  flex: 1,
  minWidth: 0,
})

const HeaderActions = styled('div')({
  alignItems: 'flex-start',
  display: 'flex',
  '& .MuiButton-root': { display: 'none' },
  '& .MuiButton-contained:not(.MuiButton-colorError)': { display: 'inline-flex', padding: '4px 8px' },
})

const HeaderButtons = styled('div')(({ theme }) => ({
  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  '& .MuiButton-root': { padding: '4px 8px' },
}))

const FooterActions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: theme.spacing(2),
  '& .MuiButton-root': { display: 'none' },
  '& .MuiButton-colorError': { display: 'inline-flex' },
}))

const FooterDelete = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: theme.spacing(2),
}))

const MobileDialog = memo(
  ({
    actions,
    children,
    content,
    mobileCloseDisabled,
    mobileDeleteButtonProps,
    mobileSaveButtonProps,
    mobileSecondaryButtonProps,
    mobileSubtitle,
    onClose,
    onMobileCancel,
    title,
    titleProps,
    ...rest
  }: AroyaDialogProps) => {
    const titleId = useId()
    const { doCloseDialogRoute } = useConnectModern('doCloseDialogRoute')

    const header = (
      <Header>
        <BackButton
          aria-label={i18n`Back`}
          color="primary"
          disabled={mobileCloseDisabled}
          onClick={onMobileCancel ?? onClose ?? (() => doCloseDialogRoute())}
        >
          <ChevronLeft fontSize="large" />
        </BackButton>
        {title ? <HeaderTitle id={titleId}>{title}</HeaderTitle> : <HeaderTitle />}
        {mobileSaveButtonProps || mobileSecondaryButtonProps ? (
          <HeaderButtons>
            {mobileSaveButtonProps ? <Button variant="contained" {...mobileSaveButtonProps} /> : null}
            {mobileSecondaryButtonProps ? <Button variant="outlined" {...mobileSecondaryButtonProps} /> : null}
          </HeaderButtons>
        ) : actions ? (
          <HeaderActions>{actions}</HeaderActions>
        ) : null}
      </Header>
    )

    return (
      <MobileWebDialog
        {...rest}
        onClose={mobileCloseDisabled ? undefined : onClose}
        aria-labelledby={title ? titleId : undefined}
        content={
          <>
            {content ?? children}
            {content !== undefined ? children : null}
            {mobileDeleteButtonProps ? (
              <FooterDelete>
                <Button color="error" variant="text" {...mobileDeleteButtonProps} />
              </FooterDelete>
            ) : actions ? (
              <FooterActions>{actions}</FooterActions>
            ) : null}
          </>
        }
        title={
          <>
            {header}
            {mobileSubtitle}
          </>
        }
        titleProps={{ ...titleProps, component: 'div' }}
      />
    )
  },
)
MobileDialog.displayName = 'MobileDialog'

export default MobileDialog
